import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Stack,
  Accordion,
  Alert,
} from "react-bootstrap";
import { FaBolt, FaChartLine, FaClipboardCheck, FaShieldAlt, FaUsers, FaPhone } from "react-icons/fa";

type Feature = {
  id: string;
  title: string;
  icon: React.ReactNode;
  desc: string;
};

type Step = {
  id: string;
  title: string;
  desc: string;
};

type Industry = {
  id: string;
  name: string;
  blurb: string;
};

type Testimonial = {
  id: string;
  quote: string;
  author: string;
  role?: string;
};

const VERSION = "v01";

const FEATURES: Feature[] = [
  {
    id: "f1",
    title: "Actionable Insights",
    icon: <FaChartLine aria-hidden />,
    desc: "See exactly where staff experience breaks and what to fix first—ranked by business impact.",
  },
  {
    id: "f2",
    title: "Fast Turnaround",
    icon: <FaBolt aria-hidden />,
    desc: "48–72 hour report delivery with prioritized recommendations and sample scripts for coaching.",
  },
  {
    id: "f3",
    title: "Verified Compliance",
    icon: <FaShieldAlt aria-hidden />,
    desc: "Evaluate phone, in-store, and web touchpoints for brand, legal, and ADA compliance.",
  },
  {
    id: "f4",
    title: "Nationwide Shoppers",
    icon: <FaUsers aria-hidden />,
    desc: "Scalable coverage with screened, trained shoppers matched to your customer profile.",
  },
];

const STEPS: Step[] = [
  { id: "s1", title: "Define Objectives", desc: "Pick outcomes to measure—speed to greet, script adherence, cross-sell, follow-up, etc." },
  { id: "s2", title: "Design Scenarios", desc: "Phone, web chat, or in-person visits—aligned to your customer journey." },
  { id: "s3", title: "Run Shops", desc: "We dispatch trained shoppers and capture evidence: audio, timestamps, and receipts." },
  { id: "s4", title: "Deliver Findings", desc: "Beautiful scorecards and clips with step-by-step fixes your team can act on this week." },
];

const INDUSTRIES: Industry[] = [
  { id: "i1", name: "Retail", blurb: "Conversion, add-ons, returns flow, and loss-prevention behaviors." },
  { id: "i2", name: "Restaurants", blurb: "Greeting, table turns, upsell cadence, cleanliness, order accuracy." },
  { id: "i3", name: "Auto Service", blurb: "Phone scripting, estimate clarity, safety inspections, timeline promises." },
  { id: "i4", name: "Financial Services", blurb: "Disclosure accuracy, product matching, wait times, KYC hygiene." },
  { id: "i5", name: "Healthcare", blurb: "Front-desk courtesy, privacy practices, eligibility & referral handling." },
  { id: "i6", name: "Hospitality", blurb: "Check-in flow, amenity briefing, housekeeping, recovery playbooks." },
];

const TESTIMONIALS: Testimonial[] = [
  {
    id: "t1",
    quote: "Within two weeks our phone close rate jumped 18%. The call clips made coaching painless.",
    author: "A. Martinez",
    role: "Director of Ops, Multi-unit Retail",
  },
  {
    id: "t2",
    quote: "They found three friction points we kept missing. The prioritized fixes paid for the program in month one.",
    author: "J. Singh",
    role: "GM, Casual Dining Group",
  },
];

const FAQ = [
  {
    q: "How fast can we launch?",
    a: "Typical kickoff to first findings is 7–10 business days, depending on scenario complexity and locations.",
  },
  {
    q: "Do you provide recordings?",
    a: "Yes. For phone and web interactions we include recordings or transcripts with timestamps when permitted.",
  },
  {
    q: "How are shoppers vetted?",
    a: "Each shopper is screened, trained on your brief, and graded for reliability before live deployment.",
  },
  {
    q: "Can you work under NDA?",
    a: "Absolutely. We frequently operate under mutual NDAs and support vendor onboarding requirements.",
  },
];

const HeroCTA: React.FC<{ onScrollToDemo: () => void }> = ({ onScrollToDemo }) => (
  <div className="py-5 py-md-6 bg-light border-bottom">
    <Container>
      <Row className="align-items-center g-4">
        <Col md={7}>
          <Badge bg="dark" className="mb-3">Secret Shopper Team</Badge>
          <h1 className="display-5 fw-semibold">
            Turn real-world customer moments into a <span className="text-primary">repeatable win</span>
          </h1>
          <p className="lead text-muted mt-3">
            We run professional mystery shops across phone, web, and in-person, then deliver clear, prioritized fixes that lift conversion and NPS.
          </p>
          <Stack direction="horizontal" gap={2} className="mt-3">
            <Button size="lg" onClick={onScrollToDemo}>Get a sample report</Button>
            <Button size="lg" variant="outline-primary" href="#contact">
              <FaPhone className="me-2" /> Talk to a specialist
            </Button>
          </Stack>
          <div className="mt-3">
            <small className="text-muted">No long contracts • Flexible locations • {new Date().getFullYear()} Edition</small>
          </div>
        </Col>
        <Col md={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Quick ROI Estimator</h5>
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label>Monthly Customer Interactions</Form.Label>
                  <Form.Control type="number" placeholder="e.g., 1,500" />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Avg. Order Value (USD)</Form.Label>
                  <Form.Control type="number" placeholder="e.g., 75" />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Conversion Lift Target</Form.Label>
                  <Form.Select defaultValue="0.05">
                    <option value="0.03">+3%</option>
                    <option value="0.05">+5%</option>
                    <option value="0.10">+10%</option>
                  </Form.Select>
                </Form.Group>
                <Button variant="primary" className="w-100">Estimate Impact</Button>
                <div className="text-muted small mt-2">We’ll refine this during your free consult.</div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </div>
);

const FeatureGrid: React.FC = () => (
  <Container className="py-5">
    <h2 className="text-center mb-4">Why teams choose us</h2>
    <Row xs={1} md={2} lg={4} className="g-4">
      {FEATURES.map((f) => (
        <Col key={f.id}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="display-6 mb-3">{f.icon}</div>
              <h5 className="mb-2">{f.title}</h5>
              <p className="text-muted mb-0">{f.desc}</p>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </Container>
);

const HowItWorks: React.FC = () => (
  <div className="bg-light py-5">
    <Container>
      <h2 className="text-center mb-4">How it works</h2>
      <Row xs={1} md={2} lg={4} className="g-4">
        {STEPS.map((s, idx) => (
          <Col key={s.id}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <Badge bg="primary" className="mb-2">Step {idx + 1}</Badge>
                <h5>{s.title}</h5>
                <p className="text-muted mb-0">{s.desc}</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  </div>
);

const Industries: React.FC = () => (
  <Container className="py-5">
    <h2 className="text-center mb-4">Industries we serve</h2>
    <Row xs={1} md={2} lg={3} className="g-4">
      {INDUSTRIES.map((i) => (
        <Col key={i.id}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h5 className="mb-1">{i.name}</h5>
              <p className="text-muted mb-0">{i.blurb}</p>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </Container>
);

const SampleDeliverable: React.FC = () => {
  const bullets = useMemo(
    () => [
      { icon: <FaClipboardCheck aria-hidden />, text: "Store-by-store scorecards with weighted metrics" },
      { icon: <FaChartLine aria-hidden />, text: "Trend charts and outlier highlights" },
      { icon: <FaShieldAlt aria-hidden />, text: "Compliance flags with evidence" },
    ],
    []
  );
  return (
    <Container id="demo" className="py-5">
      <Row className="align-items-center g-4">
        <Col md={6}>
          <h2>What your report includes</h2>
          <p className="text-muted">
            Clear findings and coach-ready clips. Use our playbooks to run weekly huddles and lock in gains.
          </p>
          <Stack gap={2} className="mt-3">
            {bullets.map((b, i) => (
              <div key={i} className="d-flex align-items-start gap-3">
                <div className="fs-3">{b.icon}</div>
                <div>{b.text}</div>
              </div>
            ))}
          </Stack>
          <Button className="mt-3" variant="primary" href="#contact">Request a sample</Button>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Img
              alt="Sample scorecard preview"
              src="https://images.unsplash.com/photo-1551281044-d8bc1ddbc0d5?q=80&w=1200&auto=format&fit=crop"
            />
            <Card.Body>
              <small className="text-muted">Representative mockup for illustration.</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const SocialProof: React.FC = () => (
  <div className="bg-light py-5">
    <Container>
      <h2 className="text-center mb-4">What operators say</h2>
      <Row xs={1} md={2} className="g-4">
        {TESTIMONIALS.map((t) => (
          <Col key={t.id}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <blockquote className="blockquote mb-3">“{t.quote}”</blockquote>
                <figcaption className="blockquote-footer mb-0">
                  {t.author} <cite className="text-muted">{t.role}</cite>
                </figcaption>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  </div>
);

const PricingCTA: React.FC = () => (
  <Container className="py-5">
    <Row className="g-4">
      <Col lg={8}>
        <Card className="h-100 shadow-sm">
          <Card.Body>
            <h3 className="mb-2">Flexible plans for any footprint</h3>
            <p className="text-muted">
              Start with a pilot (3–5 locations) or roll out nationwide. Volume pricing available.
            </p>
            <Row className="g-3">
              <Col md={6}>
                <Card className="h-100 border-primary">
                  <Card.Body>
                    <h5>Pilot</h5>
                    <div className="display-6 fw-semibold mb-2">$ —</div>
                    <ul className="mb-3">
                      <li>3–5 locations</li>
                      <li>Phone + web shops</li>
                      <li>Scorecards + clips</li>
                    </ul>
                    <Button variant="primary" href="#contact" className="w-100">Start pilot</Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100">
                  <Card.Body>
                    <h5>Scale</h5>
                    <div className="display-6 fw-semibold mb-2">Custom</div>
                    <ul className="mb-3">
                      <li>Regional or national</li>
                      <li>In-person shops</li>
                      <li>Team training kit</li>
                    </ul>
                    <Button variant="outline-primary" href="#contact" className="w-100">Request quote</Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Alert variant="secondary" className="mt-3 mb-0">
              Want a formal proposal? We’ll scope scenarios and SLAs during a 20-min discovery call.
            </Alert>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={4}>
        <Card className="h-100 shadow-sm">
          <Card.Body>
            <h5>Frequently asked</h5>
            <Accordion alwaysOpen>
              {FAQ.map((f, i) => (
                <Accordion.Item eventKey={String(i)} key={i}>
                  <Accordion.Header>{f.q}</Accordion.Header>
                  <Accordion.Body>{f.a}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </Container>
);

const ContactForm: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <div id="contact" className="bg-dark text-light py-5">
      <Container>
        <Row className="g-4">
          <Col lg={6}>
            <h2 className="mb-2">Get your sample & quote</h2>
            <p className="text-light text-opacity-75 mb-4">
              Tell us your goals and we’ll share a sample report plus a tailored plan.
            </p>
            {!submitted ? (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setSubmitted(true);
                }}
              >
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="text-uppercase small">Name</Form.Label>
                    <Form.Control required placeholder="Your name" />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="text-uppercase small">Email</Form.Label>
                    <Form.Control required type="email" placeholder="you@company.com" />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="text-uppercase small">Company</Form.Label>
                    <Form.Control placeholder="Company LLC" />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="text-uppercase small">Locations</Form.Label>
                    <Form.Select defaultValue="1-5">
                      <option>1–5</option>
                      <option>6–25</option>
                      <option>26–100</option>
                      <option>100+</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12}>
                    <Form.Label className="text-uppercase small">What do you want to improve?</Form.Label>
                    <Form.Control as="textarea" rows={4} placeholder="E.g., boost phone close rate, faster greet, better upsell…" />
                  </Col>
                  <Col xs={12}>
                    <Button variant="primary" type="submit" className="w-100">
                      Request my sample
                    </Button>
                  </Col>
                </Row>
                <div className="small mt-3 text-light text-opacity-75">
                  By submitting, you agree to be contacted about this request.
                </div>
              </Form>
            ) : (
              <Alert variant="success" className="mt-3">
                Thanks! We’ll reach out shortly with next steps and a sample deliverable.
              </Alert>
            )}
          </Col>
          <Col lg={6}>
            <Card className="bg-light text-dark h-100 shadow-sm">
              <Card.Body>
                <h5 className="mb-2">What you’ll get</h5>
                <ul className="mb-0">
                  <li>Sample scorecard + recording clip</li>
                  <li>Scope options (pilot vs scale)</li>
                  <li>Timeline & pricing ranges</li>
                  <li>Next steps to kickoff</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const Footer: React.FC = () => (
  <footer className="py-4 border-top bg-white">
    <Container>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span className="text-muted">© {new Date().getFullYear()} Secret Shopper Team</span>
        <span className="text-muted">Landing • {VERSION}</span>
      </div>
    </Container>
  </footer>
);

export const SecretShopperTeamLanding_v01: React.FC = () => {
  const scrollToDemo = () => {
    const el = document.querySelector("#demo");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Navbar expand="lg" bg="white" className="border-bottom">
        <Container>
          <Navbar.Brand href="#">
            <strong>Secret Shopper Team</strong>
          </Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse>
            <Nav className="ms-auto">
              <Nav.Link href="#demo">Sample</Nav.Link>
              <Nav.Link href="#contact">Contact</Nav.Link>
              <Button className="ms-lg-3" onClick={scrollToDemo}>
                Get a sample
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main>
        <HeroCTA onScrollToDemo={scrollToDemo} />
        <FeatureGrid />
        <HowItWorks />
        <Industries />
        <SampleDeliverable />
        <SocialProof />
        <PricingCTA />
        <ContactForm />
      </main>

      <Footer />
    </>
  );
};

export default SecretShopperTeamLanding_v01;
